import { FilterList, NavigateNext } from "@mui/icons-material";
import {
  Alert,
  Breadcrumbs,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Link as MuiLink,
  Select,
  Stack,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ArrowUpRight, ChartBar } from "@phosphor-icons/react";
import { Button } from "components/UI";
import { EthiopianDatePicker } from "components/UI/DatePicker";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getEthiopianDateWithMonthNames } from "utils/getEthiopianDateWithMonthNames";
import { useDenominationReports, useReportsTimeline } from "../api";
import { useDenominationStore } from "../store";

export const ChurchPerformanceReport = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [startDate, setStartDate] = useQueryState("startDate", parseAsString);
  const [endDate, setEndDate] = useQueryState("endDate", parseAsString);
  const [selectedTitles, setSelectedTitles] = useQueryState(
    "title",
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const [shouldFetch, setShouldFetch] = useState(false);
  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );

  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: reportsTimeline } = useReportsTimeline({
    denominationId: id
  });

  const denominationReportsQuery = useDenominationReports({
    denominationId: id,
    page,
    pageSize,
    startDate,
    endDate,
    config: {
      enabled: !!id && shouldFetch && !!startDate && !!endDate
    }
  });

  const handleTitleChange = selectedTitleIds => {
    setSelectedTitles(selectedTitleIds);

    const selectedReports = reportsTimeline?.filter(report =>
      selectedTitleIds.includes(report.id)
    );

    if (!selectedReports || selectedReports.length === 0) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    // Find earliest start date and latest end date
    const sortedByStart = [...selectedReports].sort(
      (a, b) => new Date(a.report_start_date) - new Date(b.report_start_date)
    );
    const sortedByEnd = [...selectedReports].sort(
      (a, b) => new Date(b.report_end_date) - new Date(a.report_end_date)
    );

    setStartDate(sortedByStart[0].report_start_date);
    setEndDate(sortedByEnd[0].report_end_date);
  };

  const columns = [
    {
      field: "church_name",
      headerName: t("denominations.churches.churchName"),
      flex: 1,
      valueGetter: ({ row }) =>
        row.denomination_church?.name || "No church name"
    },
    {
      field: "title",
      headerName: t("denominations.memberDashboard.reportTitle"),
      flex: 1,
      valueGetter: ({ row }) => row.title || "No title"
    },
    {
      field: "actions",
      headerName: t("denominations.churches.actions"),
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <MuiLink
          underline="hover"
          color="inherit"
          component={Link}
          to={`/forms/denomination-reporting/${selectedDenomination?.id}/${selectedDenomination?.form_token}?reportId=${params.row.id}`}
        >
          View report
        </MuiLink>
      )
    }
  ];

  return (
    <Stack py={3} spacing={5}>
      <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
        <Typography sx={{ cursor: "pointer" }} onClick={() => navigate(-1)}>
          {t("denominations.reports.reports")}
        </Typography>
        <Typography>
          {t("denominations.reports.churchPerformanceReport")}
        </Typography>
      </Breadcrumbs>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        py={2}
        sx={{
          borderTop: "1px solid #e0e0e0",
          borderBottom: "1px solid #e0e0e0"
        }}
      >
        <Stack direction="row" spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterList />
            <Typography variant="body2">
              {t("denominations.memberDashboard.filter")}
            </Typography>
          </Stack>

          <FormControl sx={{ width: "280px" }}>
            <InputLabel shrink size="small">
              {t("denominations.memberDashboard.reportTitle")}
            </InputLabel>
            <Select
              size="small"
              label={t("denominations.memberDashboard.reportTitle")}
              notched
              displayEmpty
              multiple
              value={selectedTitles}
              onChange={e => handleTitleChange(e.target.value)}
              renderValue={selected => {
                if (!selected || selected.length === 0) {
                  return (
                    <span style={{ color: "#aaa" }}>
                      {t("denominations.memberDashboard.selectOneOrMoreTitles")}
                    </span>
                  );
                }
                // Find the titles for the selected ids
                const selectedTitlesLabels = reportsTimeline
                  ?.filter(report => selected.includes(report.id))
                  .map(report => report.title || "No title");
                return selectedTitlesLabels?.join(", ");
              }}
            >
              <MenuItem value="" disabled>
                {t("denominations.memberDashboard.selectOneOrMoreTitles")}
              </MenuItem>
              {reportsTimeline?.map(report => (
                <MenuItem key={report.id} value={report.id}>
                  {report.title || t("denominations.memberDashboard.noTitle")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <EthiopianDatePicker
            label={t("denominations.memberDashboard.startDate")}
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            width="280px"
            disabled
          />
          <EthiopianDatePicker
            label={t("denominations.memberDashboard.endDate")}
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            width="280px"
            disabled
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            onClick={() => setShouldFetch(true)}
            disabled={!startDate || !endDate}
          >
            {t("denominations.memberDashboard.filter")}
          </Button>
          {startDate && endDate && (
            <Button
              flavor="outlined"
              onClick={() => {
                handleTitleChange([]);
                setShouldFetch(false);
              }}
              style={{
                borderColor: "rgba(0, 0, 0, 0.6)",
                color: "rgba(0, 0, 0, 0.6)"
              }}
            >
              {t("denominations.memberDashboard.clear")}
            </Button>
          )}
        </Stack>
      </Stack>

      {!denominationReportsQuery.data && (
        <Alert severity="info">
          {t(
            "denominations.memberDashboard.pleaseSelectOneOrMoreReportTitlesToAutomaticallyPopulateTheDateRangeThenClickTheFilterButton"
          )}
        </Alert>
      )}

      {denominationReportsQuery.data && (
        <Stack direction="row" spacing={2}>
          <Stack
            sx={{
              borderRadius: "20px",
              backgroundColor: "#0ba5ec",
              color: "#fff",
              padding: "16px",
              width: "400px",
              height: "200px",
              cursor: "pointer"
            }}
            onClick={() =>
              navigate(
                `/denomination-dashboard/${selectedDenomination?.id}/reports/summary?startDate=${startDate}&endDate=${endDate}`
              )
            }
          >
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                ml: "auto",
                bgcolor: "#fff",
                borderRadius: "50px",
                width: "40px"
              }}
            >
              <ChartBar size={24} color="#4e5ba6" />
            </Stack>
            <Stack mt="auto" mb={1} spacing={2}>
              <Typography variant="h6" fontWeight={900}>
                {t("denominations.memberDashboard.summaryReport")}
              </Typography>

              {/* Enhanced Date Range Display */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
              >
                <Chip
                  label={
                    startDate
                      ? getEthiopianDateWithMonthNames(startDate, "short")
                      : ""
                  }
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    "& .MuiChip-label": {
                      px: 1.5
                    }
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.8, fontWeight: 500 }}
                >
                  to
                </Typography>
                <Chip
                  label={
                    endDate
                      ? getEthiopianDateWithMonthNames(endDate, "short")
                      : ""
                  }
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    "& .MuiChip-label": {
                      px: 1.5
                    }
                  }}
                />
              </Stack>

              <Typography
                variant="caption"
                sx={{ opacity: 0.85, display: "block", mt: 1 }}
              >
                {t(
                  "denominations.memberDashboard.allChurchesComprehensiveSummary"
                )}
              </Typography>
            </Stack>
            <Divider sx={{ bgcolor: "rgba(255, 255, 255, 0.25)" }} />
            <Stack direction="row" alignItems="center" spacing={1} mt={1}>
              <ArrowUpRight />
              <Typography variant="body2">
                {t("demoninations.dashbord.clickToView")}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            sx={{
              borderRadius: "20px",
              backgroundColor: "#3dadb7",
              color: "#fff",
              padding: "16px",
              width: "400px",
              height: "200px"
            }}
          >
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                ml: "auto",
                bgcolor: "#fff",
                borderRadius: "50px",
                width: "40px"
              }}
            >
              <ChartBar size={24} color="#3dadb7" />
            </Stack>
            <Stack mt="auto" mb={1} spacing={1}>
              <Typography variant="h6" fontWeight={900}>
                {t("denominations.memberDashboard.totalReports")}
              </Typography>

              {/* Total Reports Count Display */}
              <Stack spacing={0.5}>
                <Typography
                  variant="h2"
                  fontWeight={900}
                  sx={{
                    fontSize: "3rem",
                    lineHeight: 1
                  }}
                >
                  {denominationReportsQuery.data?.result?.total || 0}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}

      {denominationReportsQuery.data && (
        <DataGrid
          autoHeight
          rows={denominationReportsQuery.data?.result?.data || []}
          rowCount={denominationReportsQuery.data?.result?.total || 0}
          loading={denominationReportsQuery.isLoading}
          columns={columns}
          rowsPerPageOptions={[2, 5, 10, 15, 25, 50, 100]}
          density="comfortable"
          getRowId={row => row.id}
          disableColumnMenu
          disableSelectionOnClick
          pagination
          paginationMode="server"
          page={page - 1}
          pageSize={pageSize}
          onPageChange={newPage => setPage(newPage + 1)}
          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
          localeText={{
            noRowsLabel: t("teams.teamProfile.NoRows"),
            MuiTablePagination: {
              labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
            }
          }}
        />
      )}
    </Stack>
  );
};
